'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/service-operations/sla-escalations.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/service-operations/sla-escalations)
 */

import React, { useEffect, useMemo, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  AlertTriangle,
  ArrowRight,
  CalendarX,
  CheckCircle,
  ChevronRight,
  Clock,
  Gauge,
  Info,
  Layers,
  RefreshCw,
  Settings,
  Target,
  TrendingUp,
  X,
  XCircle,
} from 'lucide-react';
import {
  Cell,
  Pie,
  PieChart,
  RadialBar,
  RadialBarChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { useTheme } from '@/contexts/ThemeContext';
import { useChartPalette } from '@/hooks/useChartPalette';
import { useBackgroundAwareInterval } from '@/hooks/useBackgroundAwareInterval';
import { authService } from '@/lib/authService';
import type { WorkItem } from '@/components/UniversalWorkItems';
import { SectionLoader } from '@/components/shared/BrandedLoadingSpinner';
import UserDocsLink from '@/components/shared/UserDocsLink';
import { getWorkItemTypeAccent } from '@/lib/work-items/typeIdentity';
import { getWorkItemsAccentPanelStyle, WorkItemsTypeAccentBadge } from '@/components/work-items/WorkItemsViewAccent';
import { isTerminalWorkItemStatus } from '@/lib/work-items/operationalStatusConfig';
import { isAnalyticsCancelledStatus, isMissedLikeStatus } from '@/lib/work-items/analyticsStatusSets';

export type SLAStatusKey = 'on_track' | 'at_risk' | 'breached' | 'completed_on_time' | 'completed_late' | 'missed';
export type SLATargetKind = 'response' | 'resolution' | 'deadline' | 'custom';
type LocalFilterValue = 'all' | SLAStatusKey | 'escalated';

export interface SLATypeTargetConfig {
  label?: string;
  response_minutes?: number | null;
  resolution_minutes?: number | null;
}

/**
 * Per-contact SLA override. When a work item has a contact_id, the resolver
 * checks for a matching override before falling back to the type-level config.
 */
export interface ContactSlaOverrideEntry {
  contact_id: number;
  item_type: string;
  item_subtype: string | null;
  enabled: boolean;
  response_minutes: number | null;
  resolution_minutes: number | null;
  breached_alert_minutes: number;
  at_risk_threshold_pct: number;
  priority_override: string | null;
}

export interface WorkItemsSLAConfig {
  enabled?: boolean;
  default_response_minutes?: number | null;
  default_resolution_minutes?: number | null;
  at_risk_threshold_pct?: number | null;
  breached_alert_minutes?: number | null;
  first_time_fix_target_pct?: number | null;
  by_type?: Record<string, SLATypeTargetConfig>;
  by_priority?: Record<string, SLATypeTargetConfig>;
}

interface WorkItemsSLAProps {
  items: WorkItem[];
  loading?: boolean;
  onViewItem?: (item: WorkItem) => void;
  activeItemType?: string | null;
  activeItemTypeLabel?: string;
  primaryColor?: string;
  compact?: boolean;
  slaConfig?: WorkItemsSLAConfig | null;
  onConfigureSLA?: () => void;
  /** When true, the component fetches its own at-risk items from the SLA API */
  embeddedMode?: boolean;
  /** Filter to specific item types when in embedded mode */
  filterItemTypes?: string[];
}

export interface SLAInsightItem {
  item: WorkItem;
  itemNumber: string;
  title: string;
  customerName?: string | null;
  priority: 'low' | 'medium' | 'high' | 'urgent' | 'critical';
  slaType: string;
  targetKind: SLATargetKind;
  slaHours: number;
  dueDateIso: string;
  timeElapsedHours: number;
  timeRemainingHours: number;
  slaStatus: SLAStatusKey;
  isCompleted: boolean;
  /** Parent work item ID (for appointments linked to jobs) */
  parentId?: string | null;
  /** IDs of child insight items that roll up into this parent */
  childInsightIds?: string[];
  /** Whether this insight was rolled up from children */
  isRollup?: boolean;
  /** Whether the backend SLA escalation scheduler has flagged this item */
  isSystemEscalated?: boolean;
  /** ISO timestamp of when the backend scheduler flagged the breach (if available) */
  escalatedAt?: string | null;
}

export interface SLASummary {
  totalTracked: number;
  onTrack: number;
  atRisk: number;
  breached: number;
  completedOnTime: number;
  completedLate: number;
  /** Appointments whose slot passed without the visit completing — scheduling failures, excluded from the compliance denominator. */
  missed: number;
  overallCompliance: number;
  avgTimeToCompletion: number;
  /** Percentage of completed items that were resolved on the first visit (no reopen/rework) */
  firstTimeFixRate: number;
}

const SLA_STATUS_CONFIG: Record<SLAStatusKey, { bg: string; text: string; icon: LucideIcon; label: string }> = {
  on_track: { bg: 'bg-green-100 dark:bg-green-900/50', text: 'text-green-700 dark:text-green-400', icon: CheckCircle, label: 'On Track' },
  at_risk: { bg: 'bg-amber-100 dark:bg-amber-900/50', text: 'text-amber-700 dark:text-amber-400', icon: AlertTriangle, label: 'At Risk' },
  breached: { bg: 'bg-red-100 dark:bg-red-900/50', text: 'text-red-700 dark:text-red-400', icon: XCircle, label: 'Breached' },
  completed_on_time: { bg: 'bg-blue-100 dark:bg-blue-900/50', text: 'text-blue-700 dark:text-blue-400', icon: CheckCircle, label: 'On Time' },
  completed_late: { bg: 'bg-orange-100 dark:bg-orange-900/50', text: 'text-orange-700 dark:text-orange-400', icon: Clock, label: 'Late' },
  missed: { bg: 'bg-rose-100 dark:bg-rose-900/50', text: 'text-rose-700 dark:text-rose-400', icon: CalendarX, label: 'Missed' },
};

const PRIORITY_CONFIG: Record<SLAInsightItem['priority'], { bg: string; text: string }> = {
  low: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-700 dark:text-gray-300' },
  medium: { bg: 'bg-blue-100 dark:bg-blue-900/50', text: 'text-blue-700 dark:text-blue-400' },
  high: { bg: 'bg-amber-100 dark:bg-amber-900/50', text: 'text-amber-700 dark:text-amber-400' },
  urgent: { bg: 'bg-red-100 dark:bg-red-900/50', text: 'text-red-700 dark:text-red-400' },
  critical: { bg: 'bg-rose-100 dark:bg-rose-900/50', text: 'text-rose-700 dark:text-rose-300' },
};

const CANCELLED_STATUSES = new Set(['cancelled', 'canceled', 'void', 'apt_cancelled', 'rescheduled']);
const SERVICE_OPERATIONS_SLA_ITEM_TYPES = new Set(['job', 'appointment']);
const SERVICE_OPERATIONS_DEFAULT_SLA_MINUTES = 240;
const APPOINTMENT_RESPONSE_STATUS_KEYS = new Set([
  'traveling',
  'travel_paused',
  'en_route',
  'on_route',
  'arrived',
  'on_site',
  'current',
  'checked_in',
  'working',
  'in_progress',
  'paused',
  'completing',
]);
const APPOINTMENT_ACTIVE_STATUS_KEYS = new Set([
  'arrived',
  'on_site',
  'current',
  'checked_in',
  'working',
  'in_progress',
  'paused',
  'completing',
]);

export const DEFAULT_WORK_ITEMS_SLA_CONFIG: WorkItemsSLAConfig = {
  enabled: true,
  default_response_minutes: 240,
  default_resolution_minutes: 2880,
  at_risk_threshold_pct: 75,
  breached_alert_minutes: 60,
  first_time_fix_target_pct: 85,
  by_type: {},
  by_priority: {},
};

function shouldUseServiceOperationsSlaDefaults(itemType?: string | null): boolean {
  return SERVICE_OPERATIONS_SLA_ITEM_TYPES.has(String(itemType || '').trim().toLowerCase());
}

export function getDefaultWorkItemsSLAConfig(itemType?: string | null): WorkItemsSLAConfig {
  const normalizedItemType = String(itemType || '').trim().toLowerCase();
  const defaultMinutes = shouldUseServiceOperationsSlaDefaults(normalizedItemType)
    ? SERVICE_OPERATIONS_DEFAULT_SLA_MINUTES
    : (DEFAULT_WORK_ITEMS_SLA_CONFIG.default_resolution_minutes ?? SERVICE_OPERATIONS_DEFAULT_SLA_MINUTES);

  return {
    enabled: true,
    default_response_minutes: DEFAULT_WORK_ITEMS_SLA_CONFIG.default_response_minutes ?? SERVICE_OPERATIONS_DEFAULT_SLA_MINUTES,
    default_resolution_minutes: defaultMinutes,
    at_risk_threshold_pct: DEFAULT_WORK_ITEMS_SLA_CONFIG.at_risk_threshold_pct,
    breached_alert_minutes: DEFAULT_WORK_ITEMS_SLA_CONFIG.breached_alert_minutes,
    first_time_fix_target_pct: DEFAULT_WORK_ITEMS_SLA_CONFIG.first_time_fix_target_pct,
    by_type: {},
    by_priority: {},
  };
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function toNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function toPositiveNumber(value: unknown): number | null {
  const parsed = toNumber(value);
  return parsed !== null && parsed > 0 ? parsed : null;
}

function toBoundedPercent(value: unknown, fallback: number): number {
  const parsed = toNumber(value);
  if (parsed === null) {
    return fallback;
  }
  return Math.min(Math.max(parsed, 1), 99);
}

export function normalizeWorkItemsSLAConfig(
  value: unknown,
  options?: { itemType?: string | null },
): WorkItemsSLAConfig {
  const record = asRecord(value);
  const fallbackConfig = getDefaultWorkItemsSLAConfig(options?.itemType);
  const rawByType = asRecord(record.by_type);
  const rawByPriority = asRecord(record.by_priority);
  const byType = Object.entries(rawByType).reduce<Record<string, SLATypeTargetConfig>>((acc, [key, target]) => {
    const targetRecord = asRecord(target);
    acc[key] = {
      label: typeof targetRecord.label === 'string' ? targetRecord.label : undefined,
      response_minutes: toPositiveNumber(targetRecord.response_minutes),
      resolution_minutes: toPositiveNumber(targetRecord.resolution_minutes),
    };
    return acc;
  }, {});
  const byPriority = Object.entries(rawByPriority).reduce<Record<string, SLATypeTargetConfig>>((acc, [key, target]) => {
    const targetRecord = asRecord(target);
    acc[key] = {
      label: typeof targetRecord.label === 'string' ? targetRecord.label : undefined,
      response_minutes: toPositiveNumber(targetRecord.response_minutes),
      resolution_minutes: toPositiveNumber(targetRecord.resolution_minutes),
    };
    return acc;
  }, {});

  return {
    enabled: record.enabled !== false,
    default_response_minutes: toPositiveNumber(record.default_response_minutes) ?? fallbackConfig.default_response_minutes,
    default_resolution_minutes: toPositiveNumber(record.default_resolution_minutes) ?? fallbackConfig.default_resolution_minutes,
    at_risk_threshold_pct: toBoundedPercent(record.at_risk_threshold_pct, fallbackConfig.at_risk_threshold_pct || 75),
    breached_alert_minutes: Math.max(toNumber(record.breached_alert_minutes) ?? fallbackConfig.breached_alert_minutes ?? 0, 0),
    first_time_fix_target_pct: toBoundedPercent(record.first_time_fix_target_pct, fallbackConfig.first_time_fix_target_pct || 85),
    by_type: byType,
    by_priority: byPriority,
  };
}

function resolveMinimumConfiguredMinutes(...values: Array<number | null | undefined>): number | null {
  const configuredValues = values.filter((value): value is number => typeof value === 'number' && Number.isFinite(value) && value > 0);
  return configuredValues.length > 0 ? Math.min(...configuredValues) : null;
}

function formatMinutesAsDuration(minutes: number | null | undefined): string {
  if (!minutes || minutes <= 0) {
    return 'Not set';
  }

  if (minutes % 1440 === 0) {
    return `${minutes / 1440}d`;
  }

  if (minutes % 60 === 0) {
    return `${minutes / 60}h`;
  }

  return `${minutes}m`;
}

function toDate(value: unknown): Date | null {
  if (typeof value !== 'string' || value.trim().length === 0) {
    return null;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function getEarliestDate(values: unknown[]): Date | null {
  let earliest: Date | null = null;

  values.forEach((value) => {
    const parsed = toDate(value);
    if (!parsed) {
      return;
    }

    if (!earliest || parsed.getTime() < earliest.getTime()) {
      earliest = parsed;
    }
  });

  return earliest;
}

function getLatestDate(values: unknown[]): Date | null {
  let latest: Date | null = null;

  values.forEach((value) => {
    const parsed = toDate(value);
    if (!parsed) {
      return;
    }

    if (!latest || parsed.getTime() > latest.getTime()) {
      latest = parsed;
    }
  });

  return latest;
}

function hoursBetween(start: Date, end: Date): number {
  return (end.getTime() - start.getTime()) / (1000 * 60 * 60);
}

function formatLabel(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function resolvePriority(value: WorkItem['priority']): SLAInsightItem['priority'] {
  switch (value) {
    case 'low':
    case 'medium':
    case 'high':
    case 'urgent':
    case 'critical':
      return value;
    default:
      return 'medium';
  }
}

function resolveSubtypeKey(item: WorkItem, customFields: Record<string, unknown>): string | null {
  const candidates = [
    item.item_subtype,
    customFields.job_type,
    customFields.appointment_type,
    customFields.service_type,
    customFields.type,
  ];

  const match = candidates.find((value) => typeof value === 'string' && value.trim().length > 0);
  return typeof match === 'string' ? match.trim() : null;
}

/**
 * Find the best matching contact SLA override for a work item.
 * Matching priority:
 *   1. Exact match on item_type + item_subtype + priority_override
 *   2. item_type + item_subtype (no priority filter)
 *   3. item_type + priority_override (subtype = NULL)
 *   4. item_type only (subtype = NULL, no priority filter)
 */
function findContactOverride(
  overrides: ContactSlaOverrideEntry[],
  itemType: string,
  itemSubtype: string | null,
  priority: string | null,
): ContactSlaOverrideEntry | null {
  if (!overrides.length) return null;

  const type = String(itemType || '').trim().toLowerCase();
  const subtype = itemSubtype ? String(itemSubtype).trim() : null;
  const prio = priority ? String(priority).trim().toLowerCase() : null;

  if (subtype && prio) {
    const exact = overrides.find(
      (o) => o.item_type === type && o.item_subtype === subtype && o.priority_override === prio,
    );
    if (exact) return exact;
  }

  if (subtype) {
    const typeSubtype = overrides.find(
      (o) => o.item_type === type && o.item_subtype === subtype && !o.priority_override,
    );
    if (typeSubtype) return typeSubtype;
  }

  if (prio) {
    const typePrio = overrides.find(
      (o) => o.item_type === type && !o.item_subtype && o.priority_override === prio,
    );
    if (typePrio) return typePrio;
  }

  const typeOnly = overrides.find(
    (o) => o.item_type === type && !o.item_subtype && !o.priority_override,
  );
  return typeOnly || null;
}

/**
 * Apply a contact SLA override to a normalized config, producing a merged config
 * that uses the override values where present and falls back to the type config
 * for fields the override doesn't set.
 */
function applyContactOverride(
  config: WorkItemsSLAConfig,
  override: ContactSlaOverrideEntry,
): WorkItemsSLAConfig {
  return {
    ...config,
    enabled: override.enabled !== false,
    default_response_minutes: override.response_minutes ?? config.default_response_minutes,
    default_resolution_minutes: override.resolution_minutes ?? config.default_resolution_minutes,
    breached_alert_minutes: override.breached_alert_minutes ?? config.breached_alert_minutes,
    at_risk_threshold_pct: override.at_risk_threshold_pct ?? config.at_risk_threshold_pct,
  };
}

function getNormalizedStatus(item: Pick<WorkItem, 'status'>): string {
  return String(item.status || 'open').trim().toLowerCase();
}

function getAppointmentOperationalResponseAt(item: WorkItem, customFields: Record<string, unknown>): Date | null {
  const normalizedStatus = getNormalizedStatus(item);

  return getEarliestDate([
    customFields.travel_started_at,
    customFields.arrived_at,
    customFields.check_in_timestamp,
    customFields.work_started_at,
    customFields.started_at,
    item.started_at,
    APPOINTMENT_RESPONSE_STATUS_KEYS.has(normalizedStatus) ? item.updated_at : null,
  ]);
}

function getEffectiveFirstRespondedAt(item: WorkItem, customFields: Record<string, unknown>): Date | null {
  return getEarliestDate([
    item.sla_first_responded_at,
    customFields.sla_first_responded_at,
    getAppointmentOperationalResponseAt(item, customFields)?.toISOString(),
  ]);
}

function getEffectiveCompletedAt(item: WorkItem, customFields: Record<string, unknown>): Date | null {
  const normalizedStatus = getNormalizedStatus(item);

  return getLatestDate([
    item.completed_at,
    item.closed_at,
    customFields.completed_at,
    customFields.closed_at,
    customFields.field_action_completed_at,
    isTerminalWorkItemStatus(normalizedStatus, item.item_type) ? item.updated_at : null,
  ]);
}

function shouldDeferFutureScheduledAppointment(
  item: WorkItem,
  now: Date,
  scheduledStart: Date | null,
  firstRespondedAt: Date | null,
  completedAt: Date | null,
): boolean {
  if (item.item_type !== 'appointment' || !scheduledStart) {
    return false;
  }

  return scheduledStart.getTime() > now.getTime()
    && !firstRespondedAt
    && !completedAt
    && !APPOINTMENT_ACTIVE_STATUS_KEYS.has(getNormalizedStatus(item));
}

type ResolvedTrackedTarget = {
  kind: SLATargetKind;
  dueAt: Date;
  budgetHours: number;
  label: string;
  startAt: Date;
};

function resolveTrackedTarget(
  item: WorkItem,
  createdAt: Date,
  now: Date,
  slaConfig?: WorkItemsSLAConfig | null,
): ResolvedTrackedTarget | null {
  const customFields = asRecord(item.custom_fields);
  const normalizedConfig = normalizeWorkItemsSLAConfig(
    slaConfig || getDefaultWorkItemsSLAConfig(item.item_type),
    { itemType: item.item_type },
  );
  const firstRespondedAt = getEffectiveFirstRespondedAt(item, customFields);
  const completedAt = getEffectiveCompletedAt(item, customFields);
  const responseDeadline = toDate(item.sla_first_response_deadline) || toDate(customFields.sla_first_response_deadline);
  const scheduledStart = toDate(item.scheduled_start);
  const scheduledEnd = toDate(item.scheduled_end);
  const subtypeKey = resolveSubtypeKey(item, customFields);
  const subtypeConfig = subtypeKey ? normalizedConfig.by_type?.[subtypeKey] : undefined;
  const priorityKey = resolvePriority(item.priority);
  const priorityConfig = priorityKey ? normalizedConfig.by_priority?.[priorityKey] : undefined;
  const configuredResponseMinutes = resolveMinimumConfiguredMinutes(
    subtypeConfig?.response_minutes,
    priorityConfig?.response_minutes,
  ) ?? normalizedConfig.default_response_minutes;
  const responseMinutes = toNumber(item.sla_response_time)
    ?? toNumber(customFields.sla_response_time)
    ?? configuredResponseMinutes;

  if (shouldDeferFutureScheduledAppointment(item, now, scheduledStart, firstRespondedAt, completedAt)) {
    return null;
  }

  if (item.item_type === 'appointment' && scheduledStart) {
    if (!firstRespondedAt && !completedAt) {
      const scheduleResponseMinutes = responseMinutes || normalizedConfig.default_response_minutes || SERVICE_OPERATIONS_DEFAULT_SLA_MINUTES;
      const effectiveStartAt = scheduledStart.getTime() > createdAt.getTime() ? createdAt : scheduledStart;
      return {
        kind: 'deadline',
        dueAt: scheduledStart,
        budgetHours: scheduleResponseMinutes / 60,
        label: 'Scheduled Start',
        startAt: effectiveStartAt,
      };
    }

    if (scheduledEnd) {
      const startAt = firstRespondedAt || scheduledStart;
      return {
        kind: 'deadline',
        dueAt: scheduledEnd,
        budgetHours: Math.max(hoursBetween(startAt, scheduledEnd), 0.25),
        label: 'Scheduled Window',
        startAt,
      };
    }
  }

  if (!firstRespondedAt) {
    const responseStartAt = scheduledStart && scheduledStart.getTime() > createdAt.getTime()
      ? scheduledStart
      : createdAt;
    const dueAt = responseDeadline || (responseMinutes ? new Date(responseStartAt.getTime() + (responseMinutes * 60 * 1000)) : null);
    const budgetHours = responseMinutes
      ? responseMinutes / 60
      : (dueAt ? hoursBetween(responseStartAt, dueAt) : null);

    if (dueAt && budgetHours && budgetHours > 0) {
      return {
        kind: 'response',
        dueAt,
        budgetHours,
        label: 'Response SLA',
        startAt: responseStartAt,
      };
    }
  }

  const dueDate = toDate(item.sla_resolution_deadline) || toDate(item.due_date) || toDate(customFields.sla_resolution_deadline);
  const customHours = toNumber(customFields.sla_hours) ?? toNumber(customFields.sla_resolution_hours);
  const configuredResolutionMinutes = resolveMinimumConfiguredMinutes(
    subtypeConfig?.resolution_minutes,
    priorityConfig?.resolution_minutes,
  ) ?? normalizedConfig.default_resolution_minutes;
  const resolutionMinutes = toNumber(item.sla_resolution_time)
    ?? toNumber(customFields.sla_resolution_time)
    ?? configuredResolutionMinutes;
  const customType = typeof customFields.sla_type === 'string' && customFields.sla_type.trim().length > 0
    ? customFields.sla_type.trim()
    : null;
  const resolvedConfigLabel = [subtypeConfig?.label, priorityConfig?.label]
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    .join(' • ');

  const resolutionStartAt = scheduledStart && scheduledStart.getTime() > createdAt.getTime()
    ? scheduledStart
    : createdAt;

  const derivedDueAt = dueDate
    || (customHours ? new Date(resolutionStartAt.getTime() + (customHours * 60 * 60 * 1000)) : null)
    || (resolutionMinutes ? new Date(resolutionStartAt.getTime() + (resolutionMinutes * 60 * 1000)) : null);

  const budgetHours = customHours
    ?? (resolutionMinutes ? resolutionMinutes / 60 : (derivedDueAt ? hoursBetween(resolutionStartAt, derivedDueAt) : null));

  if (!derivedDueAt || !budgetHours || budgetHours <= 0) {
    return null;
  }

  return {
    kind: customHours ? 'custom' : (dueDate ? 'deadline' : 'resolution'),
    dueAt: derivedDueAt,
    budgetHours,
    label: customType || resolvedConfigLabel || (dueDate ? 'Deadline' : 'Resolution SLA'),
    startAt: resolutionStartAt,
  };
}

function buildSLAInsightItem(
  item: WorkItem,
  now: Date,
  slaConfig?: WorkItemsSLAConfig | null,
  configItemType?: string | null,
  configMap?: Map<string, WorkItemsSLAConfig> | null,
  contactOverrideMap?: Map<number, ContactSlaOverrideEntry[]> | null,
): SLAInsightItem | null {
  const normalizedStatus = getNormalizedStatus(item);
  if (CANCELLED_STATUSES.has(normalizedStatus) || isAnalyticsCancelledStatus(normalizedStatus)) {
    return null;
  }

  const createdAt = toDate(item.created_at);
  if (!createdAt) {
    return null;
  }

  // Prefer a per-type config from the map (client-configured SLA for each item type).
  // Fall back to the single slaConfig if the map doesn't have an entry for this type.
  // If the single config was fetched for a different item type (e.g. job config
  // applied to an appointment), use type-appropriate defaults instead to avoid
  // by_type/by_priority overrides bleeding across item types.
  let effectiveConfig: WorkItemsSLAConfig;
  if (configMap && item.item_type && configMap.has(item.item_type)) {
    effectiveConfig = configMap.get(item.item_type)!;
  } else if (configItemType && item.item_type && configItemType !== item.item_type) {
    effectiveConfig = getDefaultWorkItemsSLAConfig(item.item_type);
  } else {
    effectiveConfig = slaConfig || getDefaultWorkItemsSLAConfig(item.item_type);
  }

  // Apply per-contact SLA override if one exists for this work item's contact
  const contactId = typeof item.contact_id === 'number' && item.contact_id > 0
    ? item.contact_id
    : null;
  if (contactId && contactOverrideMap && contactOverrideMap.has(contactId)) {
    const customFields = asRecord(item.custom_fields);
    const subtypeKey = resolveSubtypeKey(item, customFields);
    const priorityKey = resolvePriority(item.priority);
    const override = findContactOverride(
      contactOverrideMap.get(contactId)!,
      item.item_type,
      subtypeKey,
      priorityKey,
    );
    if (override && override.enabled !== false) {
      effectiveConfig = applyContactOverride(effectiveConfig, override);
    }
  }

  const normalizedConfig = normalizeWorkItemsSLAConfig(
    effectiveConfig,
    { itemType: item.item_type },
  );
  if (normalizedConfig.enabled === false) {
    return null;
  }

  const target = resolveTrackedTarget(item, createdAt, now, normalizedConfig);
  if (!target) {
    return null;
  }

  const completedAt = getEffectiveCompletedAt(item, asRecord(item.custom_fields));
  const isCompleted = Boolean(completedAt) || isTerminalWorkItemStatus(normalizedStatus, item.item_type);
  const completionComparisonDate = completedAt || toDate(item.updated_at) || now;
  const elapsedHours = Math.max(hoursBetween(target.startAt, isCompleted ? completionComparisonDate : now), 0);
  const timeRemaining = hoursBetween(now, target.dueAt);
  const breachFlag = target.kind === 'response'
    ? item.sla_response_breached === true
    : item.sla_resolution_breached === true;

  // Detect backend scheduler escalation: the sla_*_breached flags are set by the
  // pg-boss system-sla-escalation cron. If a breached_at timestamp is present,
  // the backend has confirmed the breach (vs client-side real-time computation).
  const customFields = asRecord(item.custom_fields);
  const responseBreachedBySystem = item.sla_response_breached === true
    && Boolean(item.sla_response_breached_at || customFields.sla_response_breached_at);
  const resolutionBreachedBySystem = item.sla_resolution_breached === true
    && Boolean(item.sla_resolution_breached_at || customFields.sla_resolution_breached_at);
  const isSystemEscalated = responseBreachedBySystem || resolutionBreachedBySystem;
  const escalatedAt = responseBreachedBySystem
    ? (item.sla_response_breached_at || customFields.sla_response_breached_at as string || null)
    : (resolutionBreachedBySystem
      ? (item.sla_resolution_breached_at || customFields.sla_resolution_breached_at as string || null)
      : null);

  const progressPercent = target.budgetHours > 0
    ? Math.min(Math.max((elapsedHours / target.budgetHours) * 100, 0), 100)
    : 0;

  let slaStatus: SLAStatusKey;
  if (isMissedLikeStatus(normalizedStatus)) {
    // Missed / no-show is a scheduling failure — distinct from a late
    // completion and never a compliant outcome.
    slaStatus = 'missed';
  } else if (isCompleted) {
    slaStatus = completionComparisonDate.getTime() <= target.dueAt.getTime()
      ? 'completed_on_time'
      : 'completed_late';
  } else if (breachFlag || timeRemaining <= 0) {
    slaStatus = 'breached';
  } else if (progressPercent >= (normalizedConfig.at_risk_threshold_pct || 75)) {
    slaStatus = 'at_risk';
  } else {
    slaStatus = 'on_track';
  }

  return {
    item,
    itemNumber: item.item_number || `${String(item.item_type || 'WI').toUpperCase()}-${String(item.id).slice(0, 8)}`,
    title: item.title?.trim() || 'Untitled work item',
    customerName: item.contact_name || null,
    priority: resolvePriority(item.priority),
    slaType: target.label,
    targetKind: target.kind,
    slaHours: target.budgetHours,
    dueDateIso: target.dueAt.toISOString(),
    timeElapsedHours: elapsedHours,
    timeRemainingHours: Math.max(timeRemaining, 0),
    slaStatus,
    isCompleted,
    parentId: item.parent_id ?? null,
    isSystemEscalated,
    escalatedAt: escalatedAt ?? null,
  };
}

// ============================================================================
// SLA STATUS SEVERITY RANKING (higher = worse)
// Used for rolling up child appointment SLA statuses into parent job SLAs
// ============================================================================
const SLA_STATUS_SEVERITY: Record<SLAStatusKey, number> = {
  completed_on_time: 0,
  on_track: 1,
  completed_late: 2,
  at_risk: 3,
  breached: 4,
  missed: 5,
};

function pickWorseStatus(a: SLAStatusKey, b: SLAStatusKey): SLAStatusKey {
  return SLA_STATUS_SEVERITY[a] >= SLA_STATUS_SEVERITY[b] ? a : b;
}

/**
 * Build a map of parent item ID → child work items, using both `parent_id`
 * on the WorkItem and `parent_work_item_id` / `linkage_work_item_id` in
 * custom_fields. This is the key relationship that links appointments to
 * their parent jobs.
 */
function buildParentChildMap(items: WorkItem[]): Map<string, WorkItem[]> {
  const childrenByParent = new Map<string, WorkItem[]>();

  for (const item of items) {
    const parentId = item.parent_id
      || asRecord(item.custom_fields).parent_work_item_id as string
      || null;

    if (parentId && typeof parentId === 'string') {
      const children = childrenByParent.get(parentId);
      if (children) {
        children.push(item);
      } else {
        childrenByParent.set(parentId, [item]);
      }
    }
  }

  return childrenByParent;
}

/**
 * Build SLA insights with parent-child rollup. When a job has appointments,
 * the job's SLA status is the worst of its own status and its children's
 * statuses. This prevents a job from appearing "on track" when one of its
 * appointments has breached.
 *
 * The returned list includes both parent and child insights. Child insights
 * have `parentId` set. Parent insights that were rolled up from children
 * have `isRollup` set to true and `childInsightIds` populated.
 *
 * For the summary, use `buildWorkItemsSLASummary` with `dedupeChildren: true`
 * to avoid double-counting parent and child items.
 */
export function buildWorkItemsSLAInsights(
  items: WorkItem[],
  slaConfig?: WorkItemsSLAConfig | null,
  now: Date = new Date(),
  configItemType?: string | null,
  configMap?: Map<string, WorkItemsSLAConfig> | null,
  contactOverrideMap?: Map<number, ContactSlaOverrideEntry[]> | null,
): SLAInsightItem[] {
  const rawInsights = items
    .map((item) => buildSLAInsightItem(item, now, slaConfig, configItemType, configMap, contactOverrideMap))
    .filter((item): item is SLAInsightItem => Boolean(item));

  return rollupParentChildInsights(rawInsights, items);
}

/**
 * Roll up child appointment SLA statuses into parent job SLA insights.
 * - Parent jobs that have tracked child appointments get their status
 *   upgraded to the worst status among themselves and their children.
 * - Parent insights get `childInsightIds` populated and `isRollup` set.
 * - Child insights retain their individual `parentId` for display.
 */
function rollupParentChildInsights(
  insights: SLAInsightItem[],
  originalItems: WorkItem[],
): SLAInsightItem[] {
  if (insights.length === 0) return insights;

  // Build parent→children map from original items
  const childrenByParent = buildParentChildMap(originalItems);

  // Build insight ID → insight map
  const insightById = new Map<string, SLAInsightItem>();
  for (const insight of insights) {
    insightById.set(insight.item.id, insight);
  }

  // For each parent that has children, roll up
  for (const [parentId, children] of childrenByParent) {
    const parentInsight = insightById.get(parentId);
    if (!parentInsight) continue;

    // Find which children have SLA insights
    const childInsights = children
      .map((child) => insightById.get(child.id))
      .filter((ci): ci is SLAInsightItem => Boolean(ci));

    if (childInsights.length === 0) continue;

    // Record child insight IDs on the parent
    parentInsight.childInsightIds = childInsights.map((ci) => ci.item.id);
    parentInsight.isRollup = true;

    // Roll up: parent status = worst of parent + all children
    let rolledUpStatus = parentInsight.slaStatus;
    for (const childInsight of childInsights) {
      rolledUpStatus = pickWorseStatus(rolledUpStatus, childInsight.slaStatus);
    }
    parentInsight.slaStatus = rolledUpStatus;

    // If any child is not completed, the parent is not fully completed
    const allChildrenCompleted = childInsights.every((ci) => ci.isCompleted);
    if (!allChildrenCompleted && parentInsight.isCompleted) {
      parentInsight.isCompleted = false;
      // Re-evaluate status: if was completed_on_time but children incomplete,
      // downgrade to on_track or at_risk based on elapsed time
      if (rolledUpStatus === 'completed_on_time') {
        parentInsight.slaStatus = 'on_track';
      }
    }

    // Use the earliest due date among parent and children for display
    const allDueDates = [parentInsight, ...childInsights]
      .map((ci) => new Date(ci.dueDateIso).getTime())
      .filter((t) => Number.isFinite(t));
    if (allDueDates.length > 0) {
      const earliestDue = Math.min(...allDueDates);
      parentInsight.dueDateIso = new Date(earliestDue).toISOString();
    }
  }

  return insights;
}

/**
 * Build SLA summary from tracked insight items.
 *
 * When `dedupeChildren` is true (default), child items that have been rolled
 * up into a parent are excluded from the summary counts to prevent
 * double-counting. The parent's rolled-up status is used instead.
 */
export function buildWorkItemsSLASummary(
  trackedItems: SLAInsightItem[],
  options?: { dedupeChildren?: boolean },
): SLASummary {
  const dedupeChildren = options?.dedupeChildren !== false;

  // When deduping, exclude child insights that have a parent in the tracked set
  const parentIdSet = new Set(
    trackedItems
      .filter((item) => !item.parentId)
      .map((item) => item.item.id),
  );

  const summaryItems = dedupeChildren
    ? trackedItems.filter((item) => {
        // Keep parent items (no parentId) always
        if (!item.parentId) return true;
        // Keep child items only if their parent is NOT in the tracked set
        return !parentIdSet.has(item.parentId);
      })
    : trackedItems;

  const activeItems = summaryItems.filter((item) => !item.isCompleted);
  // Missed items are terminal but not completions — keep them out of the
  // compliance/average-completion math entirely.
  const completedItems = summaryItems.filter((item) => item.isCompleted && item.slaStatus !== 'missed');
  const missed = summaryItems.filter((item) => item.slaStatus === 'missed').length;
  const onTrack = activeItems.filter((item) => item.slaStatus === 'on_track').length;
  const atRisk = activeItems.filter((item) => item.slaStatus === 'at_risk').length;
  const breached = activeItems.filter((item) => item.slaStatus === 'breached').length;
  const completedOnTime = completedItems.filter((item) => item.slaStatus === 'completed_on_time').length;
  const completedLate = completedItems.filter((item) => item.slaStatus === 'completed_late').length;
  const totalCompleted = completedOnTime + completedLate;
  const overallCompliance = totalCompleted > 0 ? (completedOnTime / totalCompleted) * 100 : 100;
  const avgTimeToCompletion = completedItems.length > 0
    ? completedItems.reduce((sum, item) => sum + item.timeElapsedHours, 0) / completedItems.length
    : 0;
  // First-time-fix rate: items completed on time without rework.
  // Approximated as completed_on_time / total_completed (items that needed
  // rework typically finish late, so on-time completion is a reasonable proxy).
  const firstTimeFixRate = totalCompleted > 0 ? (completedOnTime / totalCompleted) * 100 : 0;

  return {
    totalTracked: summaryItems.length,
    onTrack,
    atRisk,
    breached,
    completedOnTime,
    completedLate,
    missed,
    overallCompliance,
    avgTimeToCompletion,
    firstTimeFixRate,
  };
}

function SLAStatusBadge({ status }: { status: SLAStatusKey }) {
  const config = SLA_STATUS_CONFIG[status];
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${config.bg} ${config.text}`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </span>
  );
}

function PriorityBadge({ priority }: { priority: SLAInsightItem['priority'] }) {
  const config = PRIORITY_CONFIG[priority];

  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${config.bg} ${config.text}`}>
      {formatLabel(priority)}
    </span>
  );
}

function EscalatedBadge({ escalatedAt }: { escalatedAt?: string | null }) {
  let tooltip = 'Flagged by SLA escalation scheduler';
  if (escalatedAt) {
    const date = new Date(escalatedAt);
    if (!Number.isNaN(date.getTime())) {
      const minutesAgo = Math.round((Date.now() - date.getTime()) / (1000 * 60));
      if (minutesAgo < 60) {
        tooltip = `Auto-escalated ${minutesAgo}m ago`;
      } else if (minutesAgo < 1440) {
        tooltip = `Auto-escalated ${Math.floor(minutesAgo / 60)}h ago`;
      } else {
        tooltip = `Auto-escalated ${Math.floor(minutesAgo / 1440)}d ago`;
      }
    }
  }

  return (
    <span
      title={tooltip}
      className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-900/50 dark:text-purple-400"
    >
      <TrendingUp className="h-3 w-3" />
      Escalated
    </span>
  );
}

function TimeProgress({ elapsed, total }: { elapsed: number; total: number }) {
  const percentage = total > 0 ? Math.min((elapsed / total) * 100, 100) : 0;
  const remaining = Math.max(total - elapsed, 0);

  let barColor = 'bg-green-500';
  if (percentage >= 100) barColor = 'bg-red-500';
  else if (percentage >= 75) barColor = 'bg-amber-500';
  else if (percentage >= 50) barColor = 'bg-yellow-500';

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-gray-500 dark:text-gray-400">{elapsed.toFixed(1)}h elapsed</span>
        <span className={remaining <= 0 ? 'text-red-500' : 'text-gray-500 dark:text-gray-400'}>
          {remaining > 0 ? `${remaining.toFixed(1)}h left` : 'Overdue'}
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
        <div className={`h-full rounded-full transition-all duration-300 ${barColor}`} style={{ width: `${percentage}%` }} />
      </div>
    </div>
  );
}

function ComplianceGauge({ value }: { value: number }) {
  const chart = useChartPalette();

  const getColor = (metric: number) => {
    if (metric >= 95) return '#10b981';
    if (metric >= 85) return '#f59e0b';
    return '#ef4444';
  };

  const data = [{ value, fill: getColor(value) }];

  return (
    <div className="relative">
      <ResponsiveContainer width="100%" height={160}>
        <RadialBarChart
          innerRadius="60%"
          outerRadius="100%"
          data={data}
          startAngle={180}
          endAngle={0}
          cx="50%"
          cy="80%"
        >
          <RadialBar
            background={{ fill: chart.radialTrackFill }}
            dataKey="value"
            cornerRadius={10}
          />
        </RadialBarChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 flex flex-col items-center justify-end pb-4">
        <span className={`text-3xl font-bold ${chart.isDark ? 'text-white' : 'text-gray-900'}`}>{value.toFixed(1)}%</span>
        <span className={`text-sm ${chart.isDark ? 'text-gray-400' : 'text-gray-500'}`}>Compliance</span>
      </div>
    </div>
  );
}

function AlertCard({
  title,
  count,
  type,
  items,
  onViewItem,
}: {
  title: string;
  count: number;
  type: 'danger' | 'warning' | 'info' | 'escalated';
  items: SLAInsightItem[];
  onViewItem?: (item: WorkItem) => void;
}) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.mode === 'dark';
  const [expanded, setExpanded] = useState(false);

  const colors = {
    danger: { bg: 'bg-red-50 dark:bg-red-900/30', border: 'border-red-200 dark:border-red-800', icon: 'text-red-500' },
    warning: { bg: 'bg-amber-50 dark:bg-amber-900/30', border: 'border-amber-200 dark:border-amber-800', icon: 'text-amber-500' },
    info: { bg: 'bg-blue-50 dark:bg-blue-900/30', border: 'border-blue-200 dark:border-blue-800', icon: 'text-blue-500' },
    escalated: { bg: 'bg-purple-50 dark:bg-purple-900/30', border: 'border-purple-200 dark:border-purple-800', icon: 'text-purple-500' },
  };

  const config = colors[type];

  return (
    <div className={`rounded-xl border ${config.bg} ${config.border}`}>
      <button type="button" onClick={() => setExpanded((value) => !value)} className="flex w-full items-center justify-between p-4">
        <div className="flex items-center gap-3">
          {type === 'danger' && <XCircle className={`h-5 w-5 ${config.icon}`} />}
          {type === 'warning' && <AlertTriangle className={`h-5 w-5 ${config.icon}`} />}
          {type === 'info' && <Info className={`h-5 w-5 ${config.icon}`} />}
          {type === 'escalated' && <TrendingUp className={`h-5 w-5 ${config.icon}`} />}
          <div className="text-left">
            <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{title}</p>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{count} item{count !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <ChevronRight className={`h-5 w-5 transition-transform ${expanded ? 'rotate-90' : ''} ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
      </button>

      {expanded && items.length > 0 && (
        <div className={`border-t p-2 ${config.border}`}>
          {items.slice(0, 5).map((entry) => (
            <button
              key={entry.item.id}
              type="button"
              onClick={() => onViewItem?.(entry.item)}
              className={`w-full rounded-lg p-2 text-left ${isDark ? 'hover:bg-white/5' : 'hover:bg-black/5'}`}
            >
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                <div className="min-w-0">
                  <p className={`truncate text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{entry.title}</p>
                  <p className={`truncate text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{entry.itemNumber}</p>
                </div>
                <div className="shrink-0 text-left sm:text-right">
                  <p className={`text-sm ${entry.timeRemainingHours <= 0 ? 'text-red-500' : isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {entry.timeRemainingHours > 0 ? `${entry.timeRemainingHours.toFixed(1)}h left` : 'Overdue'}
                  </p>
                  <PriorityBadge priority={entry.priority} />
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// AI-Powered SLA Insights Section
// ============================================================================

interface AiInsightData {
  work_item_id: string;
  similar_breaches: Array<{
    work_item_id: string;
    item_number: string;
    title: string;
    item_type: string;
    sla_type: string;
    breached_at: string | null;
    similarity: number;
  }>;
  risk_narrative: {
    narrative: string;
    risk_level: 'low' | 'medium' | 'high';
    contributing_factors: string[];
  } | null;
  finding_linkages: Array<{
    finding_code: string;
    finding_label: string;
    severity: string;
    sla_risk_contribution: number;
  }>;
  composite_risk_score: number;
  cached?: boolean;
  cache_age_minutes?: number;
}

const SLA_INSIGHTS_EXPANDED_KEY = 'ta_sla_insights_expanded';

function SLAAiInsightsSection({ items, isDark }: { items: SLAInsightItem[]; isDark: boolean }) {
  const [expanded, setExpanded] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return sessionStorage.getItem(SLA_INSIGHTS_EXPANDED_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState<Record<string, AiInsightData>>({});
  const [error, setError] = useState<string | null>(null);

  const fetchInsights = async (force = false) => {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        setLoading(false);
        return;
      }

      const results: Record<string, AiInsightData> = {};
      await Promise.all(
        items.slice(0, 3).map(async (item) => {
          try {
            const url = `/api/v1/work-items/${encodeURIComponent(item.item.id)}/sla-insights${force ? '?force=true' : ''}`;
            const response = await fetch(url, {
              headers: { Authorization: `Bearer ${token}` },
            });
            const data = await response.json();
            if (data?.success && data.data) {
              results[item.item.id] = data.data;
            }
          } catch {
            // Silent fail for individual items
          }
        }),
      );
      setInsights(results);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load AI insights');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (expanded && Object.keys(insights).length === 0 && !loading) {
      void fetchInsights();
    }
  }, [expanded]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleExpand = () => {
    const next = !expanded;
    setExpanded(next);
    try {
      sessionStorage.setItem(SLA_INSIGHTS_EXPANDED_KEY, String(next));
    } catch {
      // Ignore sessionStorage errors
    }
  };

  const handleRefresh = () => {
    void fetchInsights(true);
  };

  // Determine if all loaded insights are from cache
  const insightValues = Object.values(insights);
  const allCached = insightValues.length > 0 && insightValues.every((i) => i.cached);
  const oldestCacheMinutes = insightValues.length > 0
    ? Math.max(...insightValues.map((i) => i.cache_age_minutes ?? 0))
    : 0;

  return (
    <div className={`rounded-xl border ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
      <button
        type="button"
        onClick={handleToggleExpand}
        className={`flex w-full items-center justify-between gap-2 p-4 text-left transition-colors ${isDark ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}`}
      >
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-purple-600 dark:text-purple-400" />
          <div>
            <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>AI SLA Insights</h3>
            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Past delays, risk summary, and issues affecting this job
              {allCached && oldestCacheMinutes > 0 && (
                <span className="ml-1 text-gray-400 dark:text-gray-500">
                  · cached {oldestCacheMinutes}m ago
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {expanded && !loading && insightValues.length > 0 && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); handleRefresh(); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); handleRefresh(); } }}
              className={`flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium transition-colors ${
                isDark ? 'text-gray-400 hover:bg-gray-700 hover:text-white' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
              }`}
              title="Refresh insights"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </span>
          )}
          <ChevronRight className={`h-5 w-5 transition-transform ${expanded ? 'rotate-90' : ''} ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 p-4 space-y-4">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <SectionLoader />
            </div>
          )}

          {error && (
            <div className={`rounded-lg p-3 text-sm ${isDark ? 'bg-red-900/20 text-red-400' : 'bg-red-50 text-red-600'}`}>
              {error}
            </div>
          )}

          {!loading && !error && Object.keys(insights).length === 0 && (
            <div className={`py-6 text-center text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              No AI insights available right now. The AI service may be temporarily unavailable.
            </div>
          )}

          {!loading && Object.entries(insights).map(([itemId, data]) => {
            const insightItem = items.find((i) => i.item.id === itemId);
            if (!insightItem) return null;

            return (
              <div key={itemId} className={`rounded-lg border p-3 ${isDark ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50'}`}>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <span className="text-xs font-bold text-gray-700 dark:text-gray-300">{insightItem.itemNumber}</span>
                    <span className="ml-2 truncate text-sm font-medium text-gray-900 dark:text-white">{insightItem.title}</span>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${
                      data.composite_risk_score >= 70
                        ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                        : data.composite_risk_score >= 40
                          ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                          : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                    }`}>
                      Risk score: {data.composite_risk_score}/100
                    </span>
                  </div>
                </div>

                {/* AI Risk Narrative */}
                {data.risk_narrative && (
                  <div className="mb-3">
                    <div className="mb-1 flex items-center gap-1.5">
                      <Info className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400" />
                      <span className="text-xs font-semibold text-purple-700 dark:text-purple-400">Why this is at risk</span>
                      <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                        data.risk_narrative.risk_level === 'high'
                          ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                          : data.risk_narrative.risk_level === 'medium'
                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                            : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                      }`}>
                        {data.risk_narrative.risk_level}
                      </span>
                    </div>
                    <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      {data.risk_narrative.narrative}
                    </p>
                    {data.risk_narrative.contributing_factors.length > 0 && (
                      <ul className="mt-1.5 space-y-0.5">
                        {data.risk_narrative.contributing_factors.map((factor, i) => (
                          <li key={i} className={`flex items-start gap-1.5 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            <span className="mt-0.5 text-purple-500">•</span>
                            <span>{factor}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {/* Similar Past Breaches */}
                {data.similar_breaches.length > 0 && (
                  <div className="mb-3">
                    <div className="mb-1 flex items-center gap-1.5">
                      <Layers className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
                      <span className="text-xs font-semibold text-blue-700 dark:text-blue-400">
                        Similar jobs that were late ({data.similar_breaches.length})
                      </span>
                    </div>
                    <div className="space-y-1">
                      {data.similar_breaches.slice(0, 3).map((breach) => (
                        <div key={breach.work_item_id} className={`flex items-center justify-between rounded px-2 py-1 text-xs ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                          <div className="min-w-0 flex-1">
                            <span className="font-medium text-gray-700 dark:text-gray-300">{breach.item_number}</span>
                            <span className="ml-1.5 truncate text-gray-500 dark:text-gray-400">{breach.title}</span>
                          </div>
                          <span className="flex-shrink-0 text-gray-400">
                            {Math.round(breach.similarity * 100)}% match
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Finding Linkages */}
                {data.finding_linkages.length > 0 && (
                  <div>
                    <div className="mb-1 flex items-center gap-1.5">
                      <AlertTriangle className="h-3.5 w-3.5 text-orange-600 dark:text-orange-400" />
                      <span className="text-xs font-semibold text-orange-700 dark:text-orange-400">
                        Issues affecting this job
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {data.finding_linkages.map((finding) => (
                        <span
                          key={finding.finding_code}
                          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${
                            finding.severity === 'high'
                              ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                              : finding.severity === 'medium'
                                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                                : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {finding.finding_label} (+{finding.sla_risk_contribution})
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* No insights available for this item */}
                {!data.risk_narrative && data.similar_breaches.length === 0 && data.finding_linkages.length === 0 && (
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    No insights available for this item.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function WorkItemsSLA({
  items,
  loading = false,
  onViewItem,
  activeItemType,
  activeItemTypeLabel,
  primaryColor = '#3b82f6',
  compact = false,
  slaConfig: providedSlaConfig,
  onConfigureSLA,
  embeddedMode = false,
  filterItemTypes,
}: WorkItemsSLAProps) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.mode === 'dark';
  const chart = useChartPalette();
  const accent = useMemo(() => getWorkItemTypeAccent(activeItemType, primaryColor), [activeItemType, primaryColor]);
  const [localStatusFilter, setLocalStatusFilter] = useState<LocalFilterValue>('all');
  const [localManagerFilter, setLocalManagerFilter] = useState<number | 'all'>('all');
  const [showStatsDetailModal, setShowStatsDetailModal] = useState(false);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<LocalFilterValue>('all');
  const [loadedSlaConfig, setLoadedSlaConfig] = useState<{ itemType: string; config: WorkItemsSLAConfig | null } | null>(null);
  const [loadedSlaConfigMap, setLoadedSlaConfigMap] = useState<Map<string, WorkItemsSLAConfig> | null>(null);
  // Per-contact SLA overrides keyed by contact_id
  const [contactOverrideMap, setContactOverrideMap] = useState<Map<number, ContactSlaOverrideEntry[]> | null>(null);
  // Proactive at-risk items fetched from the SLA at-risk API (embedded mode)
  const [atRiskItems, setAtRiskItems] = useState<Array<{
    id: string;
    item_number: string;
    title: string;
    item_type: string;
    priority: string | null;
    status: string;
    sla_first_response_deadline: string | null;
    sla_resolution_deadline: string | null;
    response_at_risk: boolean;
    resolution_at_risk: boolean;
    minutes_remaining: number;
    nearest_deadline: string | null;
    was_notified: boolean;
    manager_id?: number | null;
    manager_name?: string | null;
  }>>([]);
  const [atRiskLoading, setAtRiskLoading] = useState(false);

  // Unique item types present in the items array — used to fetch per-type SLA configs
  const uniqueItemTypes = useMemo(() => {
    const types = new Set<string>();
    for (const item of items) {
      if (item.item_type) types.add(item.item_type);
    }
    return Array.from(types);
  }, [items]);

  // Unique contact IDs present in the items array — used to fetch per-contact SLA overrides
  const uniqueContactIds = useMemo(() => {
    const ids = new Set<number>();
    for (const item of items) {
      if (typeof item.contact_id === 'number' && item.contact_id > 0) {
        ids.add(item.contact_id);
      }
    }
    return Array.from(ids);
  }, [items]);

  // Fetch per-contact SLA overrides for all contacts in the items list
  useEffect(() => {
    if (uniqueContactIds.length === 0) {
      setContactOverrideMap(null);
      return;
    }

    let cancelled = false;

    const fetchOverrides = async () => {
      try {
        const token = authService.getTokens()?.access_token;
        if (!token) return;

        // Fetch overrides for each contact in parallel (small batches)
        const map = new Map<number, ContactSlaOverrideEntry[]>();
        const batchSize = 10;
        for (let i = 0; i < uniqueContactIds.length; i += batchSize) {
          const batch = uniqueContactIds.slice(i, i + batchSize);
          const results = await Promise.all(
            batch.map(async (contactId) => {
              try {
                const res = await fetch(`/api/v1/contacts/${contactId}/sla-overrides`, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                const json = await res.json();
                if (json?.success && Array.isArray(json.data)) {
                  return [contactId, json.data] as const;
                }
              } catch {
                // Silent fail — overrides are an enhancement
              }
              return [contactId, []] as const;
            }),
          );
          for (const [contactId, overrides] of results) {
            if (Array.isArray(overrides) && overrides.length > 0) {
              map.set(contactId, overrides);
            }
          }
        }

        if (!cancelled) {
          setContactOverrideMap(map.size > 0 ? map : null);
        }
      } catch {
        // Silent fail
      }
    };

    void fetchOverrides();

    return () => { cancelled = true; };
  }, [uniqueContactIds]);

  // Fetch proactive at-risk items from the SLA at-risk API (embedded mode only).
  // This powers the "Approaching Breach" alert section.
  // Background-aware: pauses while the Capacitor app is backgrounded,
  // refreshes immediately on resume.
  useBackgroundAwareInterval(async () => {
    if (!embeddedMode) return;

    setAtRiskLoading(true);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        setAtRiskLoading(false);
        return;
      }

      const params = new URLSearchParams();
      params.set('limit', '50');
      if (filterItemTypes && filterItemTypes.length === 1) {
        params.set('item_type', filterItemTypes[0]);
      }

      const response = await fetch(`/api/v1/work-items/sla-at-risk?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data?.success && Array.isArray(data.data?.items)) {
        setAtRiskItems(data.data.items);
      }
    } catch {
      // Silent fail — at-risk items are a proactive enhancement
    } finally {
      setAtRiskLoading(false);
    }
  }, {
    intervalMs: 120000,
    runImmediately: true,
    fireOnResume: true,
    deps: [embeddedMode, filterItemTypes],
  });

  // Fetch SLA config for the active item type (used for header display)
  useEffect(() => {
    if (!activeItemType || providedSlaConfig) {
      return;
    }

    let cancelled = false;

    const loadSlaConfig = async () => {
      try {
        const token = authService.getTokens()?.access_token;
        if (!token) {
          return;
        }

        const response = await fetch(`/api/v1/job-config?item_type=${encodeURIComponent(activeItemType)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        if (!cancelled && data?.success) {
          setLoadedSlaConfig({
            itemType: activeItemType,
            config: normalizeWorkItemsSLAConfig(data.data?.sla_config, { itemType: activeItemType }),
          });
        }
      } catch {
        if (!cancelled) {
          setLoadedSlaConfig({ itemType: activeItemType, config: null });
        }
      }
    };

    void loadSlaConfig();

    return () => {
      cancelled = true;
    };
  }, [activeItemType, providedSlaConfig]);

  // Fetch SLA configs for ALL unique item types in the items array so that
  // each item (e.g. appointments) uses the client's configured SLA for its
  // own type, not the active item type's config or hardcoded defaults.
  useEffect(() => {
    if (providedSlaConfig || uniqueItemTypes.length === 0) {
      return;
    }

    let cancelled = false;

    const loadAllSlaConfigs = async () => {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        return;
      }

      const entries: Array<[string, WorkItemsSLAConfig | null]> = await Promise.all(
        uniqueItemTypes.map(async (itemType) => {
          try {
            const response = await fetch(`/api/v1/job-config?item_type=${encodeURIComponent(itemType)}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            const data = await response.json();
            if (data?.success && data.data?.sla_config) {
              return [itemType, normalizeWorkItemsSLAConfig(data.data.sla_config, { itemType })] as [string, WorkItemsSLAConfig];
            }
            return [itemType, null] as [string, WorkItemsSLAConfig | null];
          } catch {
            return [itemType, null] as [string, WorkItemsSLAConfig | null];
          }
        }),
      );

      if (!cancelled) {
        const map = new Map<string, WorkItemsSLAConfig>();
        for (const [itemType, config] of entries) {
          if (config) {
            map.set(itemType, config);
          }
        }
        setLoadedSlaConfigMap(map);
      }
    };

    void loadAllSlaConfigs();

    return () => {
      cancelled = true;
    };
  }, [uniqueItemTypes, providedSlaConfig]);

  const fetchedSlaConfig = useMemo(() => {
    if (!activeItemType || loadedSlaConfig?.itemType !== activeItemType) {
      return null;
    }

    return loadedSlaConfig.config;
  }, [activeItemType, loadedSlaConfig]);

  const effectiveSlaConfig = useMemo(
    () => normalizeWorkItemsSLAConfig(
      providedSlaConfig || fetchedSlaConfig || getDefaultWorkItemsSLAConfig(activeItemType),
      { itemType: activeItemType },
    ),
    [activeItemType, fetchedSlaConfig, providedSlaConfig],
  );
  const configuredTypeOverrideCount = useMemo(
    () => Object.values(effectiveSlaConfig.by_type || {}).filter((target) => Boolean(target.response_minutes || target.resolution_minutes)).length,
    [effectiveSlaConfig.by_type],
  );
  const configuredPriorityOverrideCount = useMemo(
    () => Object.values(effectiveSlaConfig.by_priority || {}).filter((target) => Boolean(target.response_minutes || target.resolution_minutes)).length,
    [effectiveSlaConfig.by_priority],
  );

  const trackedItems = useMemo(() => {
    return buildWorkItemsSLAInsights(items, effectiveSlaConfig, new Date(), activeItemType, loadedSlaConfigMap ?? undefined, contactOverrideMap);
  }, [activeItemType, effectiveSlaConfig, items, loadedSlaConfigMap, contactOverrideMap]);

  // Manager filter scopes the whole view — summary cards, distribution,
  // active alerts and the tracked list all reflect the selected manager.
  const managerFilteredItems = useMemo(() => {
    if (localManagerFilter === 'all') return trackedItems;
    return trackedItems.filter(
      (item) => Number(item.item.manager_id) === localManagerFilter,
    );
  }, [localManagerFilter, trackedItems]);

  const summary = useMemo<SLASummary>(() => {
    return buildWorkItemsSLASummary(managerFilteredItems, { dedupeChildren: true });
  }, [managerFilteredItems]);

  const activeAlerts = useMemo(() => ({
    breached: managerFilteredItems.filter((item) => item.slaStatus === 'breached'),
    atRisk: managerFilteredItems.filter((item) => item.slaStatus === 'at_risk'),
    urgent: managerFilteredItems.filter((item) => !item.isCompleted && (item.priority === 'urgent' || item.priority === 'critical')),
    escalated: managerFilteredItems.filter((item) => item.isSystemEscalated),
  }), [managerFilteredItems]);

  const statusDistributionData = useMemo(() => [
    { name: 'On Track', value: summary.onTrack, color: '#10b981' },
    { name: 'At Risk', value: summary.atRisk, color: '#f59e0b' },
    { name: 'Breached', value: summary.breached, color: '#ef4444' },
    { name: 'Missed', value: summary.missed, color: '#e11d48' },
    { name: 'Completed On Time', value: summary.completedOnTime, color: '#3b82f6' },
    { name: 'Completed Late', value: summary.completedLate, color: '#f97316' },
  ].filter((item) => item.value > 0), [summary]);

  const filteredTrackedItems = useMemo(() => {
    const statusRank: Record<SLAStatusKey, number> = {
      missed: 0,
      breached: 1,
      at_risk: 2,
      on_track: 3,
      completed_late: 4,
      completed_on_time: 5,
    };

    return managerFilteredItems
      .filter((item) => {
        if (localStatusFilter === 'all') return true;
        if (localStatusFilter === 'escalated') return Boolean(item.isSystemEscalated);
        return item.slaStatus === localStatusFilter;
      })
      .sort((left, right) => {
        const rankDiff = statusRank[left.slaStatus] - statusRank[right.slaStatus];
        if (rankDiff !== 0) {
          return rankDiff;
        }

        return new Date(left.dueDateIso).getTime() - new Date(right.dueDateIso).getTime();
      });
  }, [localStatusFilter, managerFilteredItems]);

  // At-risk items respect the manager filter too (embedded "Approaching
  // Breach" section — the API returns manager_id per row).
  const filteredAtRiskItems = useMemo(() => {
    if (localManagerFilter === 'all') return atRiskItems;
    return atRiskItems.filter((item) => Number(item.manager_id) === localManagerFilter);
  }, [atRiskItems, localManagerFilter]);

  // Unique managers across tracked items for the manager filter
  const managerOptions = useMemo(() => {
    const seen = new Map<number, string>();
    for (const entry of trackedItems) {
      const managerId = entry.item.manager_id != null ? Number(entry.item.manager_id) : null;
      if (managerId != null && !seen.has(managerId)) {
        seen.set(managerId, entry.item.manager_name || `User #${managerId}`);
      }
    }
    return Array.from(seen.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [trackedItems]);

  const untrackedCount = Math.max(items.length - trackedItems.length, 0);
  const headline = activeItemTypeLabel ? `${activeItemTypeLabel} SLA Tracking` : 'SLA Tracking';

  if (loading && items.length === 0) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white/70 p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900/70">
        <SectionLoader size="sm" primaryColor={primaryColor} message="Loading SLA tracking..." className="h-72" />
      </div>
    );
  }

  if (trackedItems.length === 0) {
    return (
      <div className="space-y-4">
        <div className="rounded-2xl border px-4 py-4 sm:px-5" style={getWorkItemsAccentPanelStyle(accent, 'strong')}>
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <WorkItemsTypeAccentBadge label={activeItemTypeLabel || 'Tracked items'} accent={accent} uppercase={false} />
                <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-700 dark:text-gray-300">
                  <Gauge className="h-3.5 w-3.5" />
                  SLA analytics view
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-white/45 px-2.5 py-1 text-[11px] font-medium text-gray-700 shadow-sm dark:bg-black/20 dark:text-gray-200">
                  <Clock className="h-3.5 w-3.5" />
                  Response {formatMinutesAsDuration(effectiveSlaConfig.default_response_minutes)}
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-white/45 px-2.5 py-1 text-[11px] font-medium text-gray-700 shadow-sm dark:bg-black/20 dark:text-gray-200">
                  <Target className="h-3.5 w-3.5" />
                  Resolution {formatMinutesAsDuration(effectiveSlaConfig.default_resolution_minutes)}
                </span>
                {(configuredTypeOverrideCount > 0 || configuredPriorityOverrideCount > 0) && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-white/40 px-2.5 py-1 text-[11px] font-medium text-gray-600 dark:bg-black/15 dark:text-gray-300">
                    <Info className="h-3.5 w-3.5" />
                    {configuredTypeOverrideCount} type override{configuredTypeOverrideCount === 1 ? '' : 's'} • {configuredPriorityOverrideCount} priority override{configuredPriorityOverrideCount === 1 ? '' : 's'}
                  </span>
                )}
              </div>
              <div>
                <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{headline}</h2>
                <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  This view tracks items with due dates, shared SLA fields, or custom SLA hours targets.
                </p>
              </div>
            </div>
            {onConfigureSLA && (
              <div className="flex w-full items-start xl:min-w-[220px] xl:justify-end">
                <button
                  type="button"
                  onClick={onConfigureSLA}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-white px-3.5 py-2 text-sm font-medium text-gray-800 shadow-sm transition-colors hover:bg-gray-50 sm:w-auto"
                >
                  <Settings className="h-4 w-4" />
                  Configure SLA Terms
                </button>
              </div>
            )}
          </div>
        </div>

        <div className={`rounded-2xl border p-8 text-center ${isDark ? 'border-gray-700 bg-gray-900/70' : 'border-gray-200 bg-white'}`}>
          <Target className={`mx-auto mb-3 h-12 w-12 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
          <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>No SLA-tracked items found</h3>
          <p className={`mx-auto mt-2 max-w-2xl text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Try adding a due date, resolution deadline, response deadline, resolution time, response time, or custom SLA hours to the current work items.
          </p>
          {onConfigureSLA && (
            <div className="mt-5 flex justify-center">
              <button
                type="button"
                onClick={onConfigureSLA}
                className="inline-flex items-center gap-2 rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                <Settings className="h-4 w-4" />
                Configure SLA Terms
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border px-4 py-4 sm:px-5" style={getWorkItemsAccentPanelStyle(accent, 'strong')} data-tour-id="sla-target-config">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <WorkItemsTypeAccentBadge label={activeItemTypeLabel || 'Tracked items'} accent={accent} uppercase={false} />
              <span className="inline-flex items-center gap-1 rounded-full bg-white/50 px-2.5 py-1 text-[11px] font-medium text-gray-700 shadow-sm dark:bg-black/20 dark:text-gray-200">
                <Gauge className="h-3.5 w-3.5" />
                {summary.totalTracked} tracked target{summary.totalTracked === 1 ? '' : 's'}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-white/45 px-2.5 py-1 text-[11px] font-medium text-gray-700 shadow-sm dark:bg-black/20 dark:text-gray-200">
                <Clock className="h-3.5 w-3.5" />
                Response {formatMinutesAsDuration(effectiveSlaConfig.default_response_minutes)}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-white/45 px-2.5 py-1 text-[11px] font-medium text-gray-700 shadow-sm dark:bg-black/20 dark:text-gray-200">
                <Target className="h-3.5 w-3.5" />
                Resolution {formatMinutesAsDuration(effectiveSlaConfig.default_resolution_minutes)}
              </span>
              {(configuredTypeOverrideCount > 0 || configuredPriorityOverrideCount > 0) && (
                <span className="inline-flex items-center gap-1 rounded-full bg-white/40 px-2.5 py-1 text-[11px] font-medium text-gray-600 dark:bg-black/15 dark:text-gray-300">
                  <Info className="h-3.5 w-3.5" />
                  {configuredTypeOverrideCount} type override{configuredTypeOverrideCount === 1 ? '' : 's'} • {configuredPriorityOverrideCount} priority override{configuredPriorityOverrideCount === 1 ? '' : 's'}
                </span>
              )}
              {untrackedCount > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-white/40 px-2.5 py-1 text-[11px] font-medium text-gray-600 dark:bg-black/15 dark:text-gray-300">
                  <Info className="h-3.5 w-3.5" />
                  {untrackedCount} untracked in current result set
                </span>
              )}
            </div>
            <div>
              <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{headline} <UserDocsLink docsPath="/total-operations/workflows/service-operations/sla-escalations" label="User guide: SLA management" /></h2>
              <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Monitor breach risk, due-date compliance, and urgent workload across the current filtered work items.
              </p>
            </div>
          </div>

          <div className="flex w-full min-w-0 flex-col gap-2 xl:min-w-[220px] xl:max-w-[280px]">
            {onConfigureSLA && (
              <button
                type="button"
                onClick={onConfigureSLA}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-white px-3.5 py-2 text-sm font-medium text-gray-800 shadow-sm transition-colors hover:bg-gray-50"
              >
                <Settings className="h-4 w-4" />
                Configure SLA Terms
              </button>
            )}
            <label className={`text-xs font-semibold uppercase tracking-[0.18em] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              SLA status filter
            </label>
            <select
              value={localStatusFilter}
              onChange={(event) => setLocalStatusFilter(event.target.value as LocalFilterValue)}
              className={`rounded-xl border px-3 py-2 text-sm shadow-sm ${isDark ? 'border-gray-700 bg-gray-900 text-white' : 'border-gray-300 bg-white text-gray-900'}`}
            >
              <option value="all">All SLA states</option>
              <option value="on_track">On track</option>
              <option value="at_risk">At risk</option>
              <option value="breached">Breached</option>
              <option value="escalated">⚠ Auto-escalated</option>
              <option value="completed_on_time">Completed on time</option>
              <option value="completed_late">Completed late</option>
              <option value="missed">Missed</option>
            </select>
            {managerOptions.length > 0 && (
              <>
                <label className={`text-xs font-semibold uppercase tracking-[0.18em] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Manager filter
                </label>
                <select
                  value={localManagerFilter === 'all' ? 'all' : String(localManagerFilter)}
                  onChange={(event) => setLocalManagerFilter(event.target.value === 'all' ? 'all' : Number(event.target.value))}
                  className={`rounded-xl border px-3 py-2 text-sm shadow-sm ${isDark ? 'border-gray-700 bg-gray-900 text-white' : 'border-gray-300 bg-white text-gray-900'}`}
                >
                  <option value="all">All managers</option>
                  {managerOptions.map((manager) => (
                    <option key={manager.id} value={manager.id}>{manager.name}</option>
                  ))}
                </select>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-6" data-tour-id="sla-status-cards">
        <button
          type="button"
          onClick={() => { setSelectedStatusFilter('on_track'); setShowStatsDetailModal(true); }}
          className={`w-full text-left rounded-xl border p-4 transition-all hover:scale-[1.02] hover:shadow-md ${isDark ? 'border-green-800 bg-green-900/20' : 'border-green-200 bg-green-50'}`}
        >
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <p className={`text-sm ${isDark ? 'text-green-400' : 'text-green-700'}`}>On Track</p>
          </div>
          <p className="mt-1 text-2xl font-bold text-green-600">{summary.onTrack}</p>
        </button>
        <button
          type="button"
          onClick={() => { setSelectedStatusFilter('at_risk'); setShowStatsDetailModal(true); }}
          className={`w-full text-left rounded-xl border p-4 transition-all hover:scale-[1.02] hover:shadow-md ${isDark ? 'border-amber-800 bg-amber-900/20' : 'border-amber-200 bg-amber-50'}`}
        >
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            <p className={`text-sm ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>At Risk</p>
          </div>
          <p className="mt-1 text-2xl font-bold text-amber-600">{summary.atRisk}</p>
        </button>
        <button
          type="button"
          onClick={() => { setSelectedStatusFilter('breached'); setShowStatsDetailModal(true); }}
          className={`w-full text-left rounded-xl border p-4 transition-all hover:scale-[1.02] hover:shadow-md ${isDark ? 'border-red-800 bg-red-900/20' : 'border-red-200 bg-red-50'}`}
        >
          <div className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-red-500" />
            <p className={`text-sm ${isDark ? 'text-red-400' : 'text-red-700'}`}>Breached</p>
          </div>
          <p className="mt-1 text-2xl font-bold text-red-600">{summary.breached}</p>
        </button>
        <button
          type="button"
          onClick={() => { setSelectedStatusFilter('completed_on_time'); setShowStatsDetailModal(true); }}
          className={`w-full text-left rounded-xl border p-4 transition-all hover:scale-[1.02] hover:shadow-md ${isDark ? 'border-blue-800 bg-blue-900/20' : 'border-blue-200 bg-blue-50'}`}
        >
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-blue-500" />
            <p className={`text-sm ${isDark ? 'text-blue-400' : 'text-blue-700'}`}>On Time</p>
          </div>
          <p className="mt-1 text-2xl font-bold text-blue-600">{summary.completedOnTime}</p>
        </button>
        <button
          type="button"
          onClick={() => { setSelectedStatusFilter('completed_late'); setShowStatsDetailModal(true); }}
          className={`w-full text-left rounded-xl border p-4 transition-all hover:scale-[1.02] hover:shadow-md ${isDark ? 'border-orange-800 bg-orange-900/20' : 'border-orange-200 bg-orange-50'}`}
        >
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-orange-500" />
            <p className={`text-sm ${isDark ? 'text-orange-400' : 'text-orange-700'}`}>Late</p>
          </div>
          <p className="mt-1 text-2xl font-bold text-orange-600">{summary.completedLate}</p>
        </button>
        <button
          type="button"
          onClick={() => { setSelectedStatusFilter('missed'); setShowStatsDetailModal(true); }}
          className={`w-full text-left rounded-xl border p-4 transition-all hover:scale-[1.02] hover:shadow-md ${isDark ? 'border-rose-800 bg-rose-900/20' : 'border-rose-200 bg-rose-50'}`}
        >
          <div className="flex items-center gap-2">
            <CalendarX className="h-5 w-5 text-rose-500" />
            <p className={`text-sm ${isDark ? 'text-rose-400' : 'text-rose-700'}`}>Missed</p>
          </div>
          <p className="mt-1 text-2xl font-bold text-rose-600">{summary.missed}</p>
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`} data-tour-id="sla-compliance-chart">
          <h3 className={`mb-2 flex items-center gap-2 font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <Gauge className="h-4 w-4 text-indigo-500" />
            Overall Compliance
          </h3>
          <ComplianceGauge value={summary.overallCompliance} />
          <div className="mt-2 text-center">
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Avg completion: {summary.avgTimeToCompletion.toFixed(1)} hours
            </p>
            {effectiveSlaConfig.first_time_fix_target_pct != null && (
              <p className={`mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                First-time-fix: {summary.firstTimeFixRate.toFixed(0)}% / target {effectiveSlaConfig.first_time_fix_target_pct}%
              </p>
            )}
          </div>
        </div>

        <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`} data-tour-id="sla-distribution-chart">
          <h3 className={`mb-4 flex items-center gap-2 font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <Target className="h-4 w-4 text-violet-500" />
            Status Distribution
          </h3>
          <ResponsiveContainer width="100%" height={160}>
            <PieChart>
              <Pie
                data={statusDistributionData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={65}
                paddingAngle={2}
              >
                {statusDistributionData.map((entry, index) => (
                  <Cell key={`sla-status-cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={chart.tooltipContentStyle}
                labelStyle={chart.tooltipLabelStyle}
                itemStyle={chart.tooltipItemStyle}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-2 grid grid-cols-2 gap-1">
            {statusDistributionData.map((item) => (
              <div key={item.name} className="flex items-center gap-1 text-xs">
                <div className="h-2 w-2 rounded-full" style={{ backgroundColor: item.color }} />
                <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{item.name}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-3" data-tour-id="sla-at-risk-list">
          {/* Proactive Pre-Breach Warnings — from the SLA at-risk API */}
          {embeddedMode && filteredAtRiskItems.length > 0 && (
            <div className={`rounded-xl border-2 p-4 ${isDark ? 'border-orange-800 bg-orange-900/20' : 'border-orange-300 bg-orange-50'}`}>
              <div className="mb-3 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                <h4 className="font-semibold text-orange-800 dark:text-orange-300">
                  Approaching Breach — {filteredAtRiskItems.length} item{filteredAtRiskItems.length === 1 ? '' : 's'}
                </h4>
              </div>
              <p className="mb-3 text-xs text-orange-700 dark:text-orange-400">
                These work items are approaching their SLA deadline. The backend scheduler sends proactive warnings based on the per-type pre-breach alert configuration.
              </p>
              <div className="space-y-2">
                {filteredAtRiskItems.slice(0, 10).map((item) => (
                  <div
                    key={item.id}
                    className={`flex items-center justify-between gap-3 rounded-lg border p-2.5 ${isDark ? 'border-orange-800 bg-gray-800' : 'border-orange-200 bg-white'}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-orange-700 dark:text-orange-400">
                          {item.item_number || '—'}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{item.item_type}</span>
                        {item.priority && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">· {item.priority}</span>
                        )}
                        {item.was_notified && (
                          <span className="inline-flex items-center gap-0.5 rounded-full bg-orange-100 px-1.5 py-0.5 text-[10px] font-medium text-orange-700 dark:bg-orange-900/40 dark:text-orange-300">
                            <Clock className="h-2.5 w-2.5" /> Notified
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 truncate text-sm font-medium text-gray-900 dark:text-white">{item.title}</p>
                    </div>
                    <div className="flex flex-shrink-0 flex-col items-end gap-0.5">
                      <span className={`font-mono text-sm font-bold ${item.minutes_remaining <= 30 ? 'text-red-600 dark:text-red-400' : 'text-orange-600 dark:text-orange-400'}`}>
                        {item.minutes_remaining <= 0
                          ? `${Math.abs(item.minutes_remaining)}m overdue`
                          : `${Math.floor(item.minutes_remaining / 60)}h ${item.minutes_remaining % 60}m left`}
                      </span>
                      <span className="text-[10px] text-gray-500 dark:text-gray-400">
                        {item.response_at_risk && item.resolution_at_risk
                          ? 'Response + Resolution'
                          : item.response_at_risk
                            ? 'Response SLA'
                            : 'Resolution SLA'}
                      </span>
                    </div>
                  </div>
                ))}
                {filteredAtRiskItems.length > 10 && (
                  <p className="text-center text-xs text-orange-600 dark:text-orange-400">
                    + {filteredAtRiskItems.length - 10} more approaching breach
                  </p>
                )}
              </div>
            </div>
          )}

          <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Active Alerts</h3>
          {summary.breached > 0 && (
            <AlertCard title="SLA Breached" count={summary.breached} type="danger" items={activeAlerts.breached} onViewItem={onViewItem} />
          )}
          {summary.atRisk > 0 && (
            <AlertCard title="At Risk of Breach" count={summary.atRisk} type="warning" items={activeAlerts.atRisk} onViewItem={onViewItem} />
          )}
          {activeAlerts.urgent.length > 0 && (
            <AlertCard title="Urgent Priority" count={activeAlerts.urgent.length} type="info" items={activeAlerts.urgent} onViewItem={onViewItem} />
          )}
          {activeAlerts.escalated.length > 0 && (
            <AlertCard title="Auto-Escalated by Scheduler" count={activeAlerts.escalated.length} type="escalated" items={activeAlerts.escalated} onViewItem={onViewItem} />
          )}
          {summary.breached === 0 && summary.atRisk === 0 && activeAlerts.urgent.length === 0 && activeAlerts.escalated.length === 0 && (
            <div className={`rounded-xl border p-4 text-center ${isDark ? 'border-green-800 bg-green-900/20' : 'border-green-200 bg-green-50'}`}>
              <CheckCircle className="mx-auto mb-2 h-8 w-8 text-green-500" />
              <p className="font-medium text-green-700 dark:text-green-400">All clear!</p>
              <p className="text-sm text-green-600 dark:text-green-500">No active SLA alerts</p>
            </div>
          )}
        </div>
      </div>

      {/* AI SLA Insights — past delays, risk summary, and issues affecting jobs */}
      {(activeAlerts.atRisk.length > 0 || activeAlerts.breached.length > 0) && (
        <SLAAiInsightsSection
          items={[...activeAlerts.breached, ...activeAlerts.atRisk].slice(0, 5)}
          isDark={isDark}
        />
      )}

      {!compact && (
        <div className="space-y-3">
          <div>
            <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Tracked work items</h3>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {filteredTrackedItems.length} visible • ordered by breach risk and due time
            </p>
          </div>

          {filteredTrackedItems.length === 0 ? (
            <div className={`rounded-2xl border p-10 text-center ${isDark ? 'border-gray-700 bg-gray-900/70' : 'border-gray-200 bg-white'}`}>
              <Info className={`mx-auto mb-3 h-10 w-10 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
              <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>No items match the selected SLA status filter.</p>
            </div>
          ) : (
            filteredTrackedItems.slice(0, 20).map((entry) => {
              const dueDate = new Date(entry.dueDateIso);
              const cardContent = (
                <div className={`rounded-xl border p-4 transition-colors ${isDark ? 'border-gray-700 bg-gray-800 hover:border-gray-600' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="mb-1 flex flex-wrap items-center gap-2">
                        <h3 className={`truncate font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{entry.title}</h3>
                        <PriorityBadge priority={entry.priority} />
                        <SLAStatusBadge status={entry.slaStatus} />
                        {entry.isSystemEscalated && (
                          <EscalatedBadge escalatedAt={entry.escalatedAt} />
                        )}
                        {entry.isRollup && entry.childInsightIds && entry.childInsightIds.length > 0 && (
                          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${isDark ? 'bg-indigo-900/50 text-indigo-400' : 'bg-indigo-100 text-indigo-700'}`}>
                            <Layers className="h-3 w-3" />
                            {entry.childInsightIds.length} appointment{entry.childInsightIds.length === 1 ? '' : 's'}
                          </span>
                        )}
                        {entry.parentId && (
                          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-500'}`}>
                            <ChevronRight className="h-3 w-3" />
                            Child item
                          </span>
                        )}
                      </div>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {entry.itemNumber}
                        {entry.customerName ? ` • ${entry.customerName}` : ''}
                        {' • '}
                        {formatLabel(entry.slaType)}
                        {entry.item.manager_name ? ` • Manager: ${entry.item.manager_name}` : ''}
                      </p>
                      <div className="mt-3">
                        <TimeProgress elapsed={entry.timeElapsedHours} total={entry.slaHours} />
                      </div>
                    </div>

                    <div className="flex w-full flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between md:w-auto md:justify-end">
                      <div className="text-left sm:text-right">
                        <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          SLA: {entry.slaHours.toFixed(1)}h ({formatLabel(entry.targetKind)})
                        </p>
                        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          Due: {dueDate.toLocaleString()}
                        </p>
                      </div>
                      {onViewItem && (
                        <span className={`inline-flex items-center gap-1 self-start rounded-full px-2.5 py-1 text-xs font-medium sm:self-auto ${isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-600'}`}>
                          Open
                          <ArrowRight className="h-3.5 w-3.5" />
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );

              if (!onViewItem) {
                return <div key={entry.item.id}>{cardContent}</div>;
              }

              return (
                <button key={entry.item.id} type="button" onClick={() => onViewItem(entry.item)} className="block w-full text-left">
                  {cardContent}
                </button>
              );
            })
          )}
        </div>
      )}

      {showStatsDetailModal && (
        <div className="fixed inset-0 z-[250] flex items-center justify-center bg-black/50 p-4">
          <div className={`flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl shadow-2xl ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`flex items-center justify-between border-b p-4 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <div>
                <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {SLA_STATUS_CONFIG[selectedStatusFilter as SLAStatusKey]?.label || 'SLA Details'}
                </h3>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {managerFilteredItems.filter((item) => item.slaStatus === selectedStatusFilter).length} items
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowStatsDetailModal(false)}
                className={`rounded-lg p-2 transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {managerFilteredItems
                .filter((item) => item.slaStatus === selectedStatusFilter)
                .map((entry) => {
                  const dueDate = new Date(entry.dueDateIso);
                  const cardContent = (
                    <div className={`rounded-xl border p-4 transition-colors ${isDark ? 'border-gray-700 bg-gray-800 hover:border-gray-600' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="mb-1 flex flex-wrap items-center gap-2">
                            <h3 className={`truncate font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{entry.title}</h3>
                            <PriorityBadge priority={entry.priority} />
                            <SLAStatusBadge status={entry.slaStatus} />
                            {entry.isSystemEscalated && (
                              <EscalatedBadge escalatedAt={entry.escalatedAt} />
                            )}
                          </div>
                          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {entry.itemNumber}
                            {entry.customerName ? ` • ${entry.customerName}` : ''}
                            {' • '}
                            {formatLabel(entry.slaType)}
                          </p>
                          <div className="mt-3">
                            <TimeProgress elapsed={entry.timeElapsedHours} total={entry.slaHours} />
                          </div>
                        </div>

                        <div className="flex w-full flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between md:w-auto md:justify-end">
                          <div className="text-left sm:text-right">
                            <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                              SLA: {entry.slaHours.toFixed(1)}h ({formatLabel(entry.targetKind)})
                            </p>
                            <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                              Due: {dueDate.toLocaleString()}
                            </p>
                          </div>
                          {onViewItem && (
                            <span className={`inline-flex items-center gap-1 self-start rounded-full px-2.5 py-1 text-xs font-medium sm:self-auto ${isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-600'}`}>
                              Open
                              <ArrowRight className="h-3.5 w-3.5" />
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );

                  if (!onViewItem) {
                    return <div key={entry.item.id}>{cardContent}</div>;
                  }

                  return (
                    <button key={entry.item.id} type="button" onClick={() => onViewItem(entry.item)} className="block w-full text-left">
                      {cardContent}
                    </button>
                  );
                })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
